import { helpers } from '@vuelidate/validators';

// SUP-346 — v2's own `requiredIf` trims a string value
// before checking presence; 0.7's does not. That is invisible for a non-whitespace value
// but flips a whitespace-only string ('   ') from valid (0.7) to invalid (v2) when the
// condition is true. AC-2 requires identical behaviour, so this ports 0.7's untrimmed
// `req(value)` check rather than re-exporting v2's own `requiredIf`. Call sites pass a
// function condition (zero-arg or sibling-state-arg, see the note below); the 0.7
// string-key `prop` form is rejected rather than ported — no call site needs it, and
// failing loudly on an unsupported input is safer than silently weakening validation.
export default function requiredIf(propOrFunction) {
  // SUP-346 — a 0.7-style string key (`requiredIf('field')`) is not
  // ported (see the note above): `helpers.unwrap('field')` returns the string itself,
  // which is truthy, so a silently-swallowed string argument would make the field
  // always-required with no signal anything is wrong. Fail loudly instead.
  if (typeof propOrFunction === 'string') {
    throw new Error(
      "requiredIf: the vuelidate 0.7 string-prop form (requiredIf('field')) is not ported "
      + 'to @vuelidate/core 2 — pass a function, e.g. requiredIf(() => this.field).',
    );
  }

  return {
    $validator(value, siblingState, vm) {
      // SUP-347 — 0.7's requiredIf calls the condition function with the SIBLING STATE
      // as its only argument (vuelidate/lib/validators/requiredIf.js + common.js's
      // `ref`), never the field value. Every SUP-346 consumer passed a zero-arg closure
      // so this was invisible; a `(model) => ...` consumer needs it. Parity proof:
      // `validators-parity.spec.js` "requiredIf(model => ...)".
      const conditionMet = typeof propOrFunction === 'function'
        ? propOrFunction.call(vm, siblingState)
        : helpers.unwrap(propOrFunction);

      return conditionMet ? helpers.req(value) : true;
    },
    $message: 'The value is required',
    $params: { type: 'requiredIf' },
  };
}
